/**
 * src/lib/observability/webVitals.js — browser page-speed telemetry (TICKET-1250, PLAN P1).
 *
 * Measures TTFB, FCP, LCP and INP with the native PerformanceObserver (no
 * dependency) and posts them, batched, to /api/ops?action=perf.ingest, which
 * writes app_perf_events rows (area 'page') next to the API and RAG timings.
 *
 * Sampling: 20% of page loads send everything; every load sends its slow
 * metrics (classifyLatency(ms, 'route') !== 'ok', perfThresholds.js).
 * Each metric is sent at most once per page load. Rows go out when LCP is final
 * (first input or the page goes hidden) and when the page is hidden (INP).
 * Unsupported entry types (Safari: LCP/event timing) are skipped silently.
 *
 * ponytail: INP = the longest interaction seen (not the p98 of web-vitals). Same
 * value for <50 interactions per page, which is every page this app has.
 * Route-change timing stays in nav_trace (no external hook to reuse).
 */
import { classifyLatency } from './perfThresholds.js';
import { API_BASE, getAuthHeaders } from '../api/client.js';

const SAMPLE_RATE = 0.2;
const MAX_ROWS = 50; // = server cap per request

async function postRows(rows) {
  try {
    const auth = await getAuthHeaders();
    if (!auth?.Authorization) return; // perf.ingest is authenticated-only
    await fetch(`${API_BASE}/ops`, {
      method: 'POST',
      keepalive: true,
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'perf.ingest', rows }),
    });
  } catch {
    // spine-emit-ok: page-speed telemetry is best-effort; a lost batch must never surface to the user
  }
}

/** Pure collector: sampling + batching. Exposed for tests. */
export function createVitalsReporter({ send = postRows, random = Math.random, route = () => window.location.pathname } = {}) {
  const sampled = random() < SAMPLE_RATE;
  const sent = new Set();
  let queue = [];
  return {
    sampled,
    add(step, ms, meta = null) {
      if (sent.has(step) || typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return;
      sent.add(step);
      const status = classifyLatency(ms, 'route');
      if (!sampled && status === 'ok') return;
      queue.push({
        area: 'page', module: 'web_vitals', step, duration_ms: Math.round(ms), status,
        route: String(route() || '/').slice(0, 200), meta,
      });
    },
    flush() {
      while (queue.length) send(queue.splice(0, MAX_ROWS));
    },
  };
}

let _started = false;

/** Start observing. Safe to call more than once; no-op outside a browser. */
export function initWebVitals(deps = {}) {
  if (_started && !deps.force) return null;
  const PO = deps.PerformanceObserver ?? (typeof window !== 'undefined' ? window.PerformanceObserver : undefined);
  const perf = deps.performance ?? (typeof window !== 'undefined' ? window.performance : undefined);
  const doc = deps.document ?? (typeof document !== 'undefined' ? document : undefined);
  if (!PO || !perf || !doc) return null;
  _started = true;

  const rep = createVitalsReporter(deps);
  let lcp = null;
  let inp = 0;

  const observe = (type, cb, extra = {}) => {
    try {
      new PO((list) => list.getEntries().forEach(cb)).observe({ type, buffered: true, ...extra });
    } catch {
      // spine-emit-ok: entry type unsupported on this browser (e.g. Safari LCP) — nothing to measure
    }
  };

  try {
    const nav = perf.getEntriesByType?.('navigation')?.[0];
    if (nav) rep.add('ttfb', nav.responseStart - (nav.activationStart || 0), { nav_type: nav.type || null });
  } catch {
    // spine-emit-ok: navigation timing unavailable — skip TTFB
  }
  observe('paint', (e) => { if (e.name === 'first-contentful-paint') rep.add('fcp', e.startTime); });
  observe('largest-contentful-paint', (e) => { lcp = e.startTime; });
  observe('event', (e) => { if (e.interactionId) inp = Math.max(inp, e.duration); }, { durationThreshold: 40 });

  const finalizeLcp = () => {
    if (lcp != null) rep.add('lcp', lcp);
    rep.flush();
  };
  const onHidden = () => {
    if (doc.visibilityState && doc.visibilityState !== 'hidden') return;
    finalizeLcp();
    if (inp > 0) rep.add('inp', inp);
    rep.flush();
  };
  // LCP stops updating at the first input; report it then (once).
  const onFirstInput = () => {
    finalizeLcp();
    doc.removeEventListener('pointerdown', onFirstInput, true);
    doc.removeEventListener('keydown', onFirstInput, true);
  };
  doc.addEventListener('pointerdown', onFirstInput, true);
  doc.addEventListener('keydown', onFirstInput, true);
  doc.addEventListener('visibilitychange', onHidden);
  (deps.window ?? (typeof window !== 'undefined' ? window : null))?.addEventListener?.('pagehide', onHidden);

  return rep;
}
